// ============================================================================
// PublicCheckinPage — the PUBLIC, login-free check-in screen (route
// /checkin/:token). This is what the QR code opens.
// ----------------------------------------------------------------------------
// Flow: a trainer mints a token (POST /api/sessions/{id}/checkin-qr) and shows
// the QR; the client scans it, landing here. We GET /api/checkin/{token} for a
// minimal-PII view (client FIRST name, session time, trainer/location), then a
// big "Confirm arrival" button POSTs /api/checkin/{token} to mark the session
// attended + checked in. No auth: possession of the unguessable token is the
// authorization.
//
// Mobile-first, Pulse-styled, bilingual (en / 繁中) via the `checkin` NS. An
// invalid/expired token (flat 404 from the API) shows a friendly dead-end. If
// the session is already attended on load, we surface the "already checked in"
// state instead of the confirm button. Reduced-motion-safe.
// ============================================================================
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError, checkinApi } from "../api";
import type { PublicCheckinView } from "../api";
import { Button } from "../components";
import { ClientLanguageToggle } from "../client/ClientLanguageToggle";
import { formatDateTimeLong, formatTime } from "../lib/date";

type Phase = "loading" | "ready" | "done" | "invalid" | "error";

export function PublicCheckinPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { t } = useTranslation("checkin");
  const [confirmedView, setConfirmedView] = useState<PublicCheckinView | null>(
    null,
  );

  const info = useQuery({
    queryKey: ["checkin", token],
    queryFn: ({ signal }) => checkinApi.info(token, signal),
    enabled: token.length > 0,
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: () => checkinApi.confirm(token),
    onSuccess: (res) => setConfirmedView(res),
  });

  // Resolve the current phase from the query/mutation state.
  const phase: Phase = (() => {
    if (confirmedView) return "done";
    if (info.isLoading) return "loading";
    if (info.isError) {
      const status = info.error instanceof ApiError ? info.error.status : 0;
      return status === 404 ? "invalid" : "error";
    }
    if (info.data?.attendance_status === "attended") return "done";
    return "ready";
  })();

  // The view to render details from (the confirmed echo, or the initial GET).
  const view = confirmedView ?? info.data ?? null;
  const alreadyAttended =
    !confirmedView && info.data?.attendance_status === "attended";

  return (
    <div className="relative flex min-h-screen flex-col bg-paper text-body">
      {/* Top bar: brand + language toggle */}
      <header className="flex items-center justify-between px-5 pt-[max(1rem,env(safe-area-inset-top))] sm:px-6">
        <Brand label={t("app")} />
        <ClientLanguageToggle />
      </header>

      <main className="flex flex-1 items-center justify-center px-5 py-8 sm:px-6">
        <div className="w-full max-w-md">
          {phase === "loading" && (
            <Centered>
              <Spinner />
              <p className="mt-4 text-sm font-medium text-body-soft">
                {t("loading")}
              </p>
            </Centered>
          )}

          {phase === "invalid" && (
            <DeadEnd
              tone="warning"
              title={t("invalidTitle")}
              blurb={t("invalidBlurb")}
            />
          )}

          {phase === "error" && (
            <DeadEnd
              tone="danger"
              title={t("errorTitle")}
              blurb={t("errorBlurb")}
              action={
                <Button onClick={() => void info.refetch()}>{t("retry")}</Button>
              }
            />
          )}

          {phase === "done" && view && (
            <Success
              view={view}
              title={alreadyAttended ? t("alreadyTitle") : t("successTitle")}
              blurb={alreadyAttended ? t("alreadyBlurb") : t("successBlurb")}
            />
          )}

          {phase === "ready" && view && (
            <Ready
              view={view}
              confirming={confirm.isPending}
              error={confirm.isError ? t("errorBlurb") : undefined}
              onConfirm={() => confirm.mutate()}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// --- ready (confirm) state --------------------------------------------------

function Ready({
  view,
  confirming,
  error,
  onConfirm,
}: {
  view: PublicCheckinView;
  confirming: boolean;
  error?: string;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("checkin");
  const name = view.client_first_name;
  return (
    <div className="rounded-2xl bg-surface p-6 shadow-lg ring-1 ring-line sm:p-8">
      <p className="font-display text-xs font-bold uppercase tracking-kicker text-coral">
        {t("kicker")}
      </p>
      <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tightest text-body sm:text-3xl">
        {t("title")}
      </h1>
      <p className="mt-2 text-base font-bold text-body-muted">
        {name ? t("greeting", { name }) : t("greetingGeneric")}
      </p>
      <p className="mt-1 text-sm text-body-soft">{t("blurb")}</p>

      <SessionDetails view={view} />

      {error && (
        <p className="mt-4 text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      )}

      <Button
        block
        size="lg"
        onClick={onConfirm}
        disabled={confirming}
        className="mt-6"
        iconLeft={<IconCheck />}
      >
        {confirming ? t("confirming") : t("confirm")}
      </Button>
    </div>
  );
}

// --- success state ----------------------------------------------------------

function Success({
  view,
  title,
  blurb,
}: {
  view: PublicCheckinView;
  title: string;
  blurb: string;
}) {
  return (
    <div className="rounded-2xl bg-surface p-6 text-center shadow-lg ring-1 ring-line sm:p-8">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-grad-lime text-white shadow-sm">
        <svg viewBox="0 0 24 24" className="h-9 w-9" fill="none" aria-hidden>
          <path
            d="M5 13l4 4L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tightest text-body">
        {title}
      </h1>
      <p className="mt-2 text-sm text-body-soft">{blurb}</p>
      <div className="mt-5 text-left">
        <SessionDetails view={view} />
      </div>
    </div>
  );
}

// --- shared session detail rows ---------------------------------------------

function SessionDetails({ view }: { view: PublicCheckinView }) {
  const { t } = useTranslation("checkin");
  const when = view.when;
  return (
    <dl className="mt-6 divide-y divide-line overflow-hidden rounded-xl ring-1 ring-line">
      {when && (
        <Row label={t("session.when")} value={formatDateTimeLong(when)} />
      )}
      {view.trainer && (
        <Row label={t("session.with")} value={view.trainer} />
      )}
      {view.location && (
        <Row label={t("session.location")} value={view.location} />
      )}
      {when && view.attendance_status === "attended" && (
        <Row
          label={t("kicker")}
          value={t("checkedInAt", { time: formatTime(when) })}
          accent
        />
      )}
    </dl>
  );
}

function Row({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 bg-surface px-4 py-3">
      <dt className="text-xs font-bold uppercase tracking-kicker text-body-faint">
        {label}
      </dt>
      <dd
        className={
          accent
            ? "font-display text-sm font-bold text-lime-600"
            : "font-display text-sm font-bold text-body"
        }
      >
        {value}
      </dd>
    </div>
  );
}

// --- dead-end / loading scaffolding -----------------------------------------

function DeadEnd({
  tone,
  title,
  blurb,
  action,
}: {
  tone: "warning" | "danger";
  title: string;
  blurb: string;
  action?: React.ReactNode;
}) {
  const ring = tone === "danger" ? "bg-danger" : "bg-grad-amber";
  return (
    <div className="rounded-2xl bg-surface p-6 text-center shadow-lg ring-1 ring-line sm:p-8">
      <div
        className={`mx-auto grid h-16 w-16 place-items-center rounded-full text-white shadow-sm ${ring}`}
      >
        <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" aria-hidden>
          <path
            d="M12 8v5m0 3.5h.01"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
      <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tightest text-body">
        {title}
      </h1>
      <p className="mt-2 text-sm text-body-soft">{blurb}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="inline-block h-8 w-8 animate-spin rounded-full border-[3px] border-line border-t-coral"
      aria-hidden
    />
  );
}

function Brand({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-lg font-extrabold tracking-tightest text-body">
        {label}
      </span>
    </div>
  );
}

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
